import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

export default function MapConfigSaveModal({
  isOpen,
  onClose,
  onSave,
  courses,
  selectedRegion,
  yearStart,
  yearEnd,
  yearFocus,
  activeOverlays,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [courseId, setCourseId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('atlas.saveConfigHelp'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        atlas_map_config: {
          name: name.trim(),
          atlas_region_id: selectedRegion?.id,
          year_start: yearStart,
          year_end: yearEnd,
          year_focus: yearFocus,
          active_overlays: activeOverlays,
          ...(courseId ? { configurable_type: 'Course', configurable_id: courseId } : {}),
        },
      };

      const response = await fetch('/atlas/configs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const config = await response.json();
        onSave(config);
        setName('');
        setCourseId('');
        onClose();
      } else {
        const data = await response.json();
        setError(data.errors?.join(', ') || 'Failed to save configuration.');
      }
    } catch {
      setError(t('common.networkError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div
        className={cn(
          'relative z-10 bg-white dark:bg-gray-800',
          'rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700',
          'w-full max-w-md mx-4 p-6'
        )}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('atlas.saveConfig')}
        </h3>

        {error && (
          <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('atlas.nameField')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('atlas.namePlaceholder')}
              className={cn(
                'w-full rounded-lg border border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                'px-3 py-2 text-sm text-gray-900 dark:text-white',
                'focus:outline-none focus:ring-2 focus:ring-gray-400/50'
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('atlas.region')}
            </label>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {selectedRegion?.name || t('atlas.noneSelected')}
            </p>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('atlas.yearRange')}
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {yearStart} – {yearEnd} {t('atlas.ce')}
              </p>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('atlas.focusYear')}
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {yearFocus} {t('atlas.ce')}
              </p>
            </div>
          </div>

          {courses && courses.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('atlas.attachToCourse')}
              </label>
              <select
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                className={cn(
                  'w-full rounded-lg border border-gray-300 dark:border-gray-600',
                  'bg-white dark:bg-gray-700',
                  'px-3 py-2 text-sm text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-gray-400/50'
                )}
              >
                <option value="">{t('atlas.noCourse')}</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium',
              'text-gray-600 dark:text-gray-400',
              'hover:bg-gray-100 dark:hover:bg-gray-700',
              'transition-colors'
            )}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium',
              'bg-gray-900 dark:bg-white text-white dark:text-gray-900',
              'hover:bg-gray-800 dark:hover:bg-gray-100',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
